import { DateInput, Select, TextInput } from '@octano/global-ui';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import { useIdentificationTypeLoader } from '../../../../../../../components/loaders/IdentificationTypeLoader';
import { useParameters } from '../../../../../../../hooks/useParameters';
import { useValidations } from '../../../../../../../hooks/useValidations';
import { IdentificationDocumentCode } from '../../../../../../../utils/enums/identification-document-code.enum';

const fields = {
  country: 'country',
  identification: 'identification',
  names: 'names',
  lastnamePather: 'lastnamePather',
  lastnameMother: 'lastnameMother',
  birthdate: 'birthdate',
  nationality: 'nationality',
  personalEmail: 'personalEmail',
} as const;

interface CreateUserFormPersonalFieldsProps {
  prefix: string;
  disabledInputs?: boolean;
}

const CreateUserFormPersonalFields = ({
  prefix: subPrefix,
}: CreateUserFormPersonalFieldsProps) => {
  const { t } = useTranslation();
  const prefix = `${subPrefix}.personal`;
  const {
    validateTextNotEmpty,
    validateEmail,
    msgValidations,
  } = useValidations();
  const { control, watch, setValue, getValues } = useFormContext();
  const { countryOptions, nationalityOptions } = useParameters();
  const { data } = useIdentificationTypeLoader();

  const identificationTypeId = watch('identificationTypeId');
  const isPassport =
    identificationTypeId === IdentificationDocumentCode.Passport;

  const identificationTypeOptions = useMemo(
    () =>
      (data?.identificationTypes ?? []).map((item) => ({
        value: item.id,
        label: item.name,
      })),
    [data],
  );

  useEffect(() => {
    if (!identificationTypeId) {
      return;
    }
    const matched = identificationTypeOptions.find(
      (opt) => opt.value === identificationTypeId,
    );
    if (matched && getValues('identificationTypeName') !== matched.label) {
      setValue('identificationTypeName', matched.label);
    }
  }, [identificationTypeId, identificationTypeOptions, setValue, getValues]);

  return (
    <Row>
      <Col md={isPassport ? 3 : 5}>
        <TextInput
          name="identificationTypeName"
          control={control}
          label={t(`${prefix}.identificationType`)}
          readOnly
        />
      </Col>
      {isPassport && (
        <Col md={2}>
          <Select
            name={fields.country}
            control={control}
            label={t(`${prefix}.${fields.country}`)}
            options={countryOptions}
            disabled
          />
        </Col>
      )}
      <Col md={isPassport ? 2 : 7}>
        <TextInput
          name={fields.identification}
          control={control}
          label={t(`${prefix}.${fields.identification}`)}
          readOnly
        />
      </Col>
      <Col md={7}>
        <TextInput
          name={fields.names}
          control={control}
          label={t(`${prefix}.${fields.names}`)}
          rules={{ validate: validateTextNotEmpty }}
        />
      </Col>
      <Col md={6}>
        <TextInput
          name={fields.lastnamePather}
          control={control}
          label={t(`${prefix}.${fields.lastnamePather}`)}
          rules={{ validate: validateTextNotEmpty }}
        />
      </Col>
      <Col md={6}>
        <TextInput
          name={fields.lastnameMother}
          control={control}
          label={t(`${prefix}.${fields.lastnameMother}`)}
          rules={{ validate: validateTextNotEmpty }}
        />
      </Col>
      <Col md={4}>
        <DateInput
          name={fields.birthdate}
          control={control}
          label={t(`${prefix}.${fields.birthdate}`)}
          rules={{ required: msgValidations.required }}
        />
      </Col>
      <Col md={4}>
        <Select
          name={fields.nationality}
          control={control}
          label={t(`${prefix}.${fields.nationality}`)}
          isSearchable
          options={nationalityOptions}
        />
      </Col>
      <Col md={4}>
        <TextInput
          name={fields.personalEmail}
          control={control}
          label={t(`${prefix}.${fields.personalEmail}`)}
          rules={{
            validate: {
              notEmpty: validateTextNotEmpty,
              email: validateEmail,
            },
          }}
        />
      </Col>
    </Row>
  );
};

export default CreateUserFormPersonalFields;
